/**
 * Model listy omniboksa — **płaska lista pozycji z jednym zaznaczonym indeksem**.
 *
 * ⚠ Powstało z wymiany `cmdk`. Tamta biblioteka identyfikuje zaznaczenie **wartością
 * pozycji**, a nie indeksem, i przywraca je automatycznie tylko wtedy, gdy sama filtruje
 * listę. Nasze szukanie jest **serwerowe** (fuzzy, ścięte ogonki), więc filtr musiał być
 * wyłączony — i wtedy po zmianie frazy, gdy zaznaczony element wypadał z wyników,
 * zaznaczenie wskazywało na nieistniejącą pozycję i **znikało**.
 *
 * Indeks resetowany przy każdej zmianie zbioru opcji tego problemu nie ma z definicji.
 */

/** Nagłówek grupy — widoczny, ale nie do wybrania. */
export interface OmniboxHeading {
  kind: 'heading';
  key: string;
  label: string;
}

/** Pozycja do wybrania. */
export interface OmniboxOption {
  kind: 'option';
  key: string;
  onSelect: () => void;
  /**
   * Pozycja widoczna, ale niewykonalna (np. komenda niekompletna).
   *
   * ⚠ Wyłączona pozycja **nadal bierze udział w nawigacji** i może być zaznaczona —
   * inaczej powtórzylibyśmy błąd, przez który Enter przestawał działać: `cmdk` zdejmował
   * zaznaczenie z wyłączonej pozycji i nie przywracał go po odblokowaniu.
   */
  disabled?: boolean;
}

export type OmniboxRow = OmniboxHeading | OmniboxOption;

/** Same pozycje do wybrania — nawigacja klawiaturą chodzi wyłącznie po nich. */
export function selectableOf(rows: readonly OmniboxRow[]): OmniboxOption[] {
  return rows.filter((row): row is OmniboxOption => row.kind === 'option');
}

/**
 * Nowy indeks po naciśnięciu klawisza; `null` = klawisz nie należy do listy.
 *
 * Zawijanie na obu końcach jest celowe: w krótkiej liście pod polem tekstowym „w dół
 * z ostatniej" znaczy „wróć na początek", a nie „utknij". Ta sama zasada, co w menu.
 */
export function nextIndex(key: string, current: number, count: number): number | null {
  if (count === 0) {
    return null;
  }

  switch (key) {
    case 'ArrowDown':
      return (current + 1) % count;

    case 'ArrowUp':
      return (current - 1 + count) % count;

    case 'Home':
      return 0;

    case 'End':
      return count - 1;

    default:
      return null;
  }
}

/** Identyfikator DOM pozycji — pod `aria-activedescendant`. */
export function optionDomId(listId: string, key: string): string {
  return listId + '-' + key;
}
